import Itineraries from './Itineraries';
import Typography from '@mui/material/Typography';
import CountryEmoji from './CountryEmoji';
import BudgetTabs from './BudgetTabs';
import CountryFact from './CountryFact';
import parse, { domToReact } from 'html-react-parser';
import Link from '@mui/material/Link';
import EditLocationDetails from '../General/EditLocationDetails';
import DestinationImage from './DestinationImage';
import { useState, Fragment } from 'react';
import CountryMapPosts from './CountryMapPosts';
import ItineraryMap from './ItineraryMap';
import TravelTip from './TravelTip';
import addDepositphotosLink from 'helpers/addDepositphotosLink';
import SubdivisionSlider from './SubdivisionSlider';

function atmDescription(atm) {
  if (!atm) return <></>;
  const { atmFees, feeFreeAtms, expensiveAtms, comment } = atm;
  let atmFeeDescription = 'Most ATMs charge no fees';
  if (atmFees === 2)
    atmFeeDescription = 'Most ATMs charge fees, but you can find fee-free ATMs';
  if (atmFees === 3) atmFeeDescription = 'All ATMs charge fees';
  if (comment) atmFeeDescription += `. ${comment}`;
  return (
    <>
      {atmFees && atmFeeDescription && (
        <TravelTip category="ATM Fees" value={atmFeeDescription} />
      )}
      {feeFreeAtms && feeFreeAtms.length > 0 && (
        <TravelTip category="Free ATMs" value={feeFreeAtms.join(', ')} />
      )}
      {expensiveAtms && expensiveAtms.length > 0 && (
        <TravelTip
          category="ATMs to avoid (high fees)"
          value={expensiveAtms.join(', ')}
        />
      )}
    </>
  );
}

function englishProficiencyDescription({ englishProficiency }) {
  let res = 'Moderate';
  if (englishProficiency === 5) res = 'Very High';
  if (englishProficiency === 4) res = 'High';
  if (englishProficiency === 2) res = 'Low';
  if (englishProficiency === 1) res = 'Very Low';
  return res;
}

function cardAcceptanceDescription({ cardAcceptance }) {
  let res = 'Card payment is often possible, but not always';
  if (cardAcceptance === 5) res = 'Cards are accepted universally';
  if (cardAcceptance === 4) res = 'Cards are accepted at most places';
  if (cardAcceptance === 2)
    res = 'Cash is king, card payment is only possible at major chains';
  if (cardAcceptance === 1)
    res = 'Cash is king, cards are almost never accepted';
  return res;
}

export default function CountryFacts({ locationDetails, noEdit, itineraries }) {
  const [itinerary, setItinerary] = useState(0);

  let hotelLink = `https://www.booking.com/searchresults.en.html?ss=${locationDetails.title}&aid=8014892`;
  if (
    ['th', 'ph', 'in', 'id', 'vn', 'kh', 'sg', 'my', 'mm', 'la'].indexOf(
      locationDetails.countryCode,
    ) !== -1
  )
    hotelLink = 'https://agoda.tp.st/elW8GohJ';
  let busLink = 'https://omio.tp.st/IOYADJSR';
  if (
    [
      'th',
      'ph',
      'in',
      'id',
      'vn',
      'kh',
      'sg',
      'my',
      'mm',
      'la',
      'jp',
      'lk',
      'nz',
      'cn',
      'hk',
      'au',
      'tr',
      'eg',
      'jo',
      'ma',
      'ge',
    ].indexOf(locationDetails.countryCode) !== -1
  )
    busLink = 'https://12go.tp.st/rioEGvek';
  const rentalCarLink = 'https://discovercars.tp.st/yQKbm54Z';
  const privateDriverLink = 'https://gettransfer.tp.st/6xg0KNBh';
  const taxiLink = 'https://kiwitaxi.tp.st/NnUiV5id';
  const viatorLink =
    'https://www.viator.com/?pid=P00101927&mcid=42383&medium=link';

  const reactParsed = parse(locationDetails.body, {
    replace: domNode => {
      if (domNode.name === 'a') {
        return (
          <Link href={domNode.attribs.href}>
            {domToReact(domNode.children)}
          </Link>
        );
      }
    },
  });

  return (
    <>
      <div className="tw-text-base">
        <div className="pb-2">
          <CountryEmoji countryCode={locationDetails.countryCode} isFlag />
        </div>
        <Typography
          gutterBottom
          variant="h2"
          component="h1"
          className="text-light font-weight-bold text-center tw-p-2"
          style={{
            textShadow: '1px 1px 10px #343A40',
          }}
        >
          {locationDetails.title} Travel Guide
          {!noEdit && (
            <EditLocationDetails
              countryCode={locationDetails.countryCode}
              data={locationDetails}
            />
          )}
        </Typography>
        <div className="tw-container tw-mx-auto tw-pt-2 tw-pb-4">
          <div className="lg:tw-grid lg:tw-grid-cols-3 tw-gap-4">
            {[
              {
                title: 'Quick Facts',
                content: (
                  <table className="tw-w-full">
                    <tbody>
                      <CountryFact
                        noBorder
                        icon="💰"
                        category={
                          locationDetails.currencies.length === 1
                            ? 'Currency'
                            : 'Currencies'
                        }
                        value={locationDetails.currencies.map((currency, i) => (
                          <span key={currency.name}>
                            {`${i > 0 ? ', ' : ''}${currency.name} (${
                              currency.symbol
                            })`}
                          </span>
                        ))}
                      />
                      {locationDetails.currencies.length > 0 &&
                        locationDetails.currencies[0].isoCurrencyCode !==
                          'USD' && (
                          <CountryFact
                            icon="🔄"
                            category="Exchange Rate"
                            value={locationDetails.currencies.map(currency => {
                              return (
                                <Fragment key={currency.isoCurrencyCode}>
                                  {currency.isoCurrencyCode === 'USD'
                                    ? ''
                                    : `US$1 = ${currency.symbol}${currency.dollarExchangeRate}`}
                                </Fragment>
                              );
                            })}
                          />
                        )}
                      <CountryFact
                        icon="💬"
                        category={
                          locationDetails.languages.length === 1
                            ? 'Language'
                            : 'Languages'
                        }
                        value={locationDetails.languages.join(', ')}
                      />
                      {locationDetails.languages.indexOf('English') === -1 &&
                        locationDetails.englishProficiency && (
                          <CountryFact
                            icon="🗣️"
                            category="English Fluency"
                            value={englishProficiencyDescription(
                              locationDetails.englishProficiency,
                            )}
                          />
                        )}
                      <CountryFact
                        icon="🏙️"
                        category="Capital"
                        value={locationDetails.capitalCity}
                      />
                      <CountryFact
                        icon="👥"
                        category="Population"
                        value={`${locationDetails.population} million`}
                      />
                      <CountryFact
                        icon="🚘"
                        category="Driving Side"
                        value={locationDetails.drivingSide}
                      />{' '}
                      <CountryFact
                        icon="🔌"
                        category="Plugs"
                        value={locationDetails.plugTypes.join(', ')}
                      />
                      {locationDetails.timeZones && (
                        <CountryFact
                          icon="🕑"
                          category="Time"
                          value={locationDetails.timeZones.map(
                            (timeZone, i) => (
                              <Fragment key={timeZone.utcOffset}>
                                {i > 0 ? ', ' : ''}
                                {timeZone.timeZoneName} (UTC{timeZone.utcOffset}
                                )
                              </Fragment>
                            ),
                          )}
                        />
                      )}
                      <CountryFact
                        icon="🍽️"
                        category="Must-Try"
                        value={locationDetails.mustTryDishes.join(', ')}
                      />
                    </tbody>
                  </table>
                ),
              },
              {
                // title: 'Your Budget',
                content: (
                  <>
                    {locationDetails.budget && (
                      <BudgetTabs
                        countryCode={locationDetails.countryCode}
                        budget={locationDetails.budget}
                        currency={locationDetails.currencies[0]}
                        hotelLink={hotelLink}
                        rentalCarLink={rentalCarLink}
                        busLink={busLink}
                        privateDriverLink={privateDriverLink}
                        taxiLink={taxiLink}
                        viatorLink={viatorLink}
                      />
                    )}
                  </>
                ),
              },
              {
                title: 'When to go?',
                content: (
                  <>
                    <table className="tw-w-full">
                      <tbody>
                        {locationDetails.travelTimes &&
                        locationDetails.travelTimes.length > 0 ? (
                          locationDetails.travelTimes.map(
                            ({ monthRange, seasonDescription }, i) => (
                              <CountryFact
                                noBorder={i === 0}
                                key={monthRange}
                                category={monthRange}
                                value={seasonDescription}
                              />
                            ),
                          )
                        ) : (
                          <></>
                        )}
                      </tbody>
                    </table>
                  </>
                ),
              },
              {
                title: `What's unique?`,
                content: <div className="linkUnderline">{reactParsed}</div>,
              },
              {
                content:
                  locationDetails.images &&
                  locationDetails.images.length > 0 ? (
                    <DestinationImage
                      image={locationDetails.images[0]}
                      isPrio
                    />
                  ) : (
                    <></>
                  ),
              },
              {
                content:
                  itineraries && itineraries.length > 0 ? (
                    <ItineraryMap
                      itinerary={itineraries[itinerary]}
                      locationDetails={locationDetails}
                      useBbox={
                        itinerary === 0 && locationDetails.countryCode !== 'tw'
                      }
                    />
                  ) : (
                    <></>
                  ),
              },
              {
                content:
                  itineraries && itineraries.length > 0 ? (
                    <>
                      <Itineraries
                        itineraries={itineraries}
                        locationDetails={locationDetails}
                        value={itinerary}
                        setValue={setItinerary}
                      />
                    </>
                  ) : (
                    <></>
                  ),
              },
              {
                title: 'Best Experiences', // use viator affiliate links here!
                content: (
                  <>
                    <ul className="linkUnderline tw-pl-4">
                      {locationDetails.topThingsToDo.map(thingToDo => (
                        <li key={thingToDo.activity}>
                          {thingToDo.externalLink ? (
                            <a
                              href={thingToDo.externalLink}
                              target="_blank"
                              rel="nofollow noreferrer noopener"
                            >
                              {thingToDo.activity}
                            </a>
                          ) : (
                            thingToDo.activity
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                ),
              },
              {
                content:
                  locationDetails.images &&
                  locationDetails.images.length > 1 ? (
                    <DestinationImage image={locationDetails.images[1]} />
                  ) : (
                    <></>
                  ),
              },
              {
                title: 'Fun Fact',
                content: (
                  <>
                    <p>{locationDetails.funFact}</p>
                  </>
                ),
              },
              {
                title: 'FAQ',
                content: (
                  <div className="linkUnderline">
                    {locationDetails.faq.map(faqItem => (
                      <div key={faqItem.questionName}>
                        <h5>{faqItem.questionName}</h5>
                        <p>
                          {parse(
                            faqItem.acceptedAnswerText
                              .replace(
                                /(renting a car)/gi,
                                `<a href="${rentalCarLink}">$1</a>`,
                              )
                              .replace(
                                / (rental car)/gi,
                                ` <a href="${rentalCarLink}">$1</a>`,
                              )
                              .replace(
                                / (buses)/gi,
                                ` <a href="${busLink}">$1</a>`,
                              )
                              .replace(
                                / (bus) /gi,
                                ` <a href="${busLink}">$1</a> `,
                              )
                              .replace(
                                / (train) /gi,
                                ` <a href="${busLink}">$1</a> `,
                              )
                              .replace(
                                / (trains)/gi,
                                ` <a href="${busLink}">$1</a>`,
                              )
                              .replace(
                                /(public transportation)/gi,
                                `<a href="${busLink}">$1</a>`,
                              )
                              .replace(
                                / (public transport)/gi,
                                ` <a href="${busLink}">$1</a>`,
                              )
                              .replace(
                                / (private driver)/gi,
                                ` <a href="${privateDriverLink}">$1</a>`,
                              )
                              .replace(
                                / (taxis)/gi,
                                ` <a href="${taxiLink}">$1</a>`,
                              )
                              .replace(
                                / (taxi)/gi,
                                ` <a href="${taxiLink}">$1</a>`,
                              )
                              .replace(
                                / (guided tours)/gi,
                                ` <a href="${viatorLink}">$1</a>`,
                              )
                              .replace(
                                / (guided tour)/gi,
                                ` <a href="${viatorLink}">$1</a>`,
                              ),
                            {
                              replace: domNode => {
                                if (domNode.name === 'a') {
                                  domNode.attribs.target = '_blank';
                                  domNode.attribs.rel =
                                    'nofollow noreferrer noopener';
                                }
                              },
                            },
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                ),
              },
              {
                title: 'Travel Tips',
                content: (
                  <ul className="tw-list-none tw-pl-0">
                    <TravelTip
                      category="Money"
                      value={cardAcceptanceDescription(locationDetails)}
                    />{' '}
                    {atmDescription(locationDetails.atms)}
                    {/* <CountryFact
                  category="Visa"
                  value="TODO: Client-side visa api"
                /> */}
                    {locationDetails.travelEtiquette &&
                      locationDetails.travelEtiquette.dos &&
                      locationDetails.travelEtiquette.dos.length > 0 &&
                      locationDetails.travelEtiquette.dos.map(ado => {
                        return (
                          <TravelTip key={ado} category="Do" value={ado} />
                        );
                      })}
                    {locationDetails.travelEtiquette &&
                      locationDetails.travelEtiquette.donts &&
                      locationDetails.travelEtiquette.donts.length > 0 &&
                      locationDetails.travelEtiquette.donts.map(adont => {
                        return (
                          <TravelTip
                            key={adont}
                            category="Don't"
                            value={adont}
                          />
                        );
                      })}
                  </ul>
                ),
              },
            ].map(({ title, content }, i) => {
              return (
                <div
                  key={i}
                  className={`tw-relative tw-mt-1 tw-mx-1 lg:tw-mt-0 lg:tw-mx-0 tw-row-span-1 tw-rounded-xl ${
                    i === 5
                      ? 'tw-col-span-1 lg:tw-col-span-3 lg:lg:tw-col-span-1'
                      : i === 3 || i === 6 || i === 10
                      ? 'tw-col-span-2'
                      : ''
                  } tw-bg-white tw-bg-clip-padding tw-backdrop-filter tw-backdrop-blur-md tw-bg-opacity-25 tw-border-2 tw-border-gray-200 tw-p-4`}
                >
                  <h2 className="text-white tw-text-2xl tw-font-bold">
                    {title}
                  </h2>
                  {content}
                </div>
              );
            })}
            <CountryMapPosts
              bbox={locationDetails.bbox}
              countryCode={locationDetails.countryCode}
            />
            {['it', 'fr', 'es', 'us', 'gb'].indexOf(
              locationDetails.countryCode.toLowerCase(),
            ) !== -1 && (
              <SubdivisionSlider countryCode={locationDetails.countryCode} />
            )}
          </div>
          <div className="tw-pt-2 tw-flex tw-w-full tw-text-xs tw-text-gray-300">
            <div className="tw-pl-1 tw-w-1/2">
              Disclosure: This travel guide may contain{' '}
              <Link
                href="/about/affiliate-disclosure"
                className="tw-text-primary-200 hover:tw-underline"
              >
                affiliate links
              </Link>
            </div>
            {locationDetails &&
              (locationDetails.attribution || locationDetails.coverAlt) && (
                <>
                  <div className="tw-text-right tw-w-1/2 tw-pr-1">
                    {locationDetails.coverAlt && (
                      <>Cover: {locationDetails.coverAlt}</>
                    )}
                    {locationDetails.coverAlt &&
                      locationDetails.attribution && <>; </>}
                    {locationDetails.attribution && (
                      <>
                        Photo Credits:{' '}
                        {addDepositphotosLink(locationDetails.attribution)}
                      </>
                    )}
                  </div>
                </>
              )}
          </div>
          {/* <DestinationCarousel /> */}
          {/* <h3>Best Places to visit in Italy</h3>
      <div>TODO: Vertical slider with destination images</div> */}
        </div>
      </div>
      <style jsx global>{`
        .linkUnderline a {
          text-decoration: underline !important;
          color: #80cbc4;
        }
        .linkUnderline a:hover {
          color: #26a69a;
        }
      `}</style>
    </>
  );
}
